import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Row } from "@narmi/design_system";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { Checkbox } from "@lending/components/Checkbox/Checkbox";
import { DateInput } from "@lending/components/DateInput/DateInput";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { PhoneNumberInput } from "@lending/components/PhoneNumberInput/PhoneNumberInput";
import { TaxIDInput } from "@lending/components/TaxIDInput/TaxIDInput";
import { TextInput } from "@lending/components/TextInput/TextInput";
import { useGetApplicationApplicantDetailsSensitiveQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useGetApplicationApplicantDetailsSensitiveQuery";
import { useCreateCoApplicantDetailsQuery } from "@lending/hooks/queries/CoApplicantDetails/useCreateCoApplicantDetailsQuery";
import { useUpdateCoApplicantDetailsQuery } from "@lending/hooks/queries/CoApplicantDetails/useUpdateCoApplicantDetailsQuery";
import { createSchema } from "@lending/hooks/queries/PersonalInfo/createSchema";
import { PersonalInfoFormData } from "@lending/hooks/queries/PersonalInfo/types";
import { useGetCoApplicantQuery } from "@lending/hooks/queries/useGetCoApplicantQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import styles from "./CoApplicantDetails.module.css";

interface LendingAskAdditionalField {
  name: string;
  value: boolean;
}

function isAskAdditionalFieldEnabled(fields: unknown, name: string): boolean {
  if (!Array.isArray(fields)) return false;
  return (fields as LendingAskAdditionalField[]).some(
    (field) => field?.name === name && field?.value === true
  );
}

function CoApplicantDetails() {
  const { institution } = useGetInstitutionQuery();
  const { l10n } = useLocalization();
  const { goBack, goNext } = useFlowNavigation();
  const { coApplicant, isLoading: isApplicationLoading } = useGetCoApplicantQuery();
  // SSN/DOB are no longer on the applicant read; fetch them (SSN masked) to prefill when editing an
  // existing co-applicant. Untouched fields are not resubmitted (see dirtyFields handling below).
  const { data: sensitive } = useGetApplicationApplicantDetailsSensitiveQuery(
    coApplicant?.id ?? "",
    {
      enabled: Boolean(coApplicant?.id),
    }
  );
  const askIsSpouse = isAskAdditionalFieldEnabled(
    institution.lending_ask_additional_fields,
    "is_spouse"
  );
  const baseSchema = createSchema(l10n, institution);
  const schema = askIsSpouse ? baseSchema.extend({ is_spouse: z.boolean() }) : baseSchema;
  const { createCoApplicantDetails, isPending: isCreating } = useCreateCoApplicantDetailsQuery();
  const { updateCoApplicantDetails, isPending: isUpdating } = useUpdateCoApplicantDetailsQuery();
  const { showErrorToast } = useToastsContext();

  const {
    handleSubmit,
    control,
    reset,
    setError,
    setValue,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: {
      first_name: coApplicant?.first_name ?? "",
      last_name: coApplicant?.last_name ?? "",
      tax_id: "",
      date_of_birth: "",
      phone: coApplicant?.phone ?? "",
      email: coApplicant?.email ?? "",
      ...(askIsSpouse ? { is_spouse: Boolean(coApplicant?.is_spouse) } : {}),
    },
    resolver: zodResolver(schema),
  });

  const handleApiError = (error: unknown) => {
    let hasFieldErrors = false;
    if (error && typeof error === "object") {
      Object.entries(error).forEach(([key, value]) => {
        if (key in schema.shape && Array.isArray(value) && value.length > 0) {
          hasFieldErrors = true;
          setError(key as keyof (PersonalInfoFormData & { is_spouse: boolean }), {
            message: (value as string[])[0],
          });
        }
      });
    }
    if (!hasFieldErrors) {
      showErrorToast(
        l10n.getString(
          "error-application-create",
          null,
          "There was an error creating your application. Please try again."
        )
      );
    }
  };

  useEffect(() => {
    if (coApplicant) {
      reset({
        first_name: coApplicant.first_name ?? "",
        last_name: coApplicant.last_name ?? "",
        tax_id: "",
        date_of_birth: "",
        phone: coApplicant.phone ?? "",
        email: coApplicant.email ?? "",
        ...(askIsSpouse ? { is_spouse: Boolean(coApplicant.is_spouse) } : {}),
      });
    }
  }, [coApplicant, reset, askIsSpouse]);

  // Prefill the sensitive fields once /sensitive resolves. Kept out of the reset() above so a slow
  // response can't clobber edits the user made while it was loading; the dirty guards leave any
  // field the user has already touched untouched, and setValue (without shouldDirty) keeps the
  // prefilled masked value from being treated as an edit.
  useEffect(() => {
    if (!sensitive) return;
    if (!dirtyFields.tax_id) {
      setValue("tax_id", sensitive.tax_id ?? "");
    }
    if (!dirtyFields.date_of_birth) {
      setValue("date_of_birth", sensitive.date_of_birth ?? "");
    }
  }, [sensitive, dirtyFields, setValue]);

  function onClickBack() {
    goBack();
  }

  async function onSubmit(data: PersonalInfoFormData & { is_spouse?: boolean }) {
    try {
      const isUpdating = !!coApplicant?.id;

      if (isUpdating) {
        // Include sensitive fields only when the user actually changed them. The /sensitive
        // endpoint returns a masked SSN (e.g. "***-**-1234") which must never be sent back as
        // a write value — it would fail validation and, in the old delete+recreate flow, could
        // cause data loss (applicant deleted but recreation fails).
        await updateCoApplicantDetails({
          applicantId: coApplicant.id,
          data,
          includeSensitiveFields: {
            tax_id: !!dirtyFields.tax_id,
            date_of_birth: !!dirtyFields.date_of_birth,
          },
        });
      } else {
        // Fresh creation
        await createCoApplicantDetails(data);
      }

      goNext();
    } catch (error) {
      Sentry.captureException(error);
      handleApiError(error);
    }
  }

  const isLoading = isCreating || isUpdating;

  return (
    <PageLayout isLoading={isApplicationLoading}>
      <PageContent>
        <PageHeader
          heading={l10n.getString(
            "co-applicant-details-heading",
            null,
            "Ok great, can you tell us about your co-borrower?"
          )}
          subheading={l10n.getString(
            "co-applicant-details-subheading",
            null,
            "We'll email your co-borrower for any additional information needed to process the loan."
          )}
        />
        <div className={styles.subcontentContainer}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className={styles.formContent}>
              <TextInput
                name="first_name"
                control={control}
                label={l10n.getString("personal-info-first-name-label", null, "First name")}
              />
              <TextInput
                name="last_name"
                control={control}
                label={l10n.getString("personal-info-last-name-label", null, "Last name")}
              />
              <TaxIDInput
                name="tax_id"
                control={control}
                label={l10n.getString("personal-info-ssn-label", null, "Social Security Number")}
                tooltipText={l10n.getString(
                  "ssn-tooltip-text",
                  null,
                  "We require this to prevent fraud and verify your identity."
                )}
              />
              <DateInput
                name="date_of_birth"
                control={control}
                label={l10n.getString("personal-info-dob-label", null, "Date of birth")}
              />
              <TextInput
                name="email"
                control={control}
                label={l10n.getString("personal-info-email-label", null, "Email")}
                type="email"
              />
              <PhoneNumberInput
                name="phone"
                label={l10n.getString("personal-info-phone-label", null, "Phone number")}
                control={control}
              />
              {askIsSpouse && (
                <Checkbox
                  name="is_spouse"
                  control={control}
                  label={l10n.getString(
                    "co-applicant-is-spouse-checkbox-label",
                    null,
                    "Co-borrower is the applicant's spouse"
                  )}
                />
              )}
            </div>
            <div>
              <Row alignItems="center" justifyContent="space-between">
                <BackButton onClick={onClickBack} />
                <Button
                  label={l10n.getString("button-label-next", null, "Next")}
                  type="submit"
                  isLoading={isLoading}
                />
              </Row>
            </div>
          </form>
        </div>
      </PageContent>
    </PageLayout>
  );
}

export { CoApplicantDetails };
